// Ledger-level domain rules.
//
// The ledger is APPEND-ONLY (hard rule 2). Money fields are integer paise
// (hard rule 1). Every transaction resolves to a project XOR a cost center
// (hard rule 6) — allowed to be transiently unmapped, at which point it lands
// in the queue.

import { RuleViolationError } from '../errors.js';
import type { Paise } from '../money/index.js';
import { paise, rupeesToPaise } from '../money/index.js';
import type { BusinessDate } from '../period/index.js';
import { businessDate as toBusinessDate } from '../period/index.js';

export type Direction = 'in' | 'out';

export interface MappingRefs {
  projectId?: string | null;
  costCenterId?: string | null;
}

/** Allowed: exactly one set, OR both null (unmapped — the queue handles it). Rejected: both set. */
export function assertProjectXorCostCenter(refs: MappingRefs): void {
  const hasProject = !!refs.projectId;
  const hasCostCenter = !!refs.costCenterId;
  if (hasProject && hasCostCenter) {
    throw new RuleViolationError(
      'ledger.project_xor_cost_center',
      'A transaction cannot be mapped to both a project and a cost center.',
    );
  }
}

/** Stricter — used when transitioning `status` to `mapped`: exactly one must be non-null. */
export function assertMappedProjectXorCostCenter(refs: MappingRefs): void {
  const hasProject = !!refs.projectId;
  const hasCostCenter = !!refs.costCenterId;
  if (!hasProject && !hasCostCenter) {
    throw new RuleViolationError(
      'ledger.unmapped_cannot_map',
      'Mapping a transaction requires exactly one of projectId or costCenterId.',
    );
  }
  if (hasProject && hasCostCenter) {
    throw new RuleViolationError(
      'ledger.project_xor_cost_center',
      'A transaction cannot be mapped to both a project and a cost center.',
    );
  }
}

export interface TxnLite {
  id: string;
  accountId: string;
  amountPaise: bigint;
  direction: Direction;
  reversalOfId?: string | null;
}

/**
 * A reversal row must (a) reference the original via reversalOfId, (b) target
 * the same account, (c) carry the same amount, (d) have the opposite direction.
 */
export function assertReversalPair(orig: TxnLite, rev: TxnLite): void {
  if (rev.reversalOfId !== orig.id) {
    throw new RuleViolationError(
      'ledger.reversal_link_missing',
      'Reversal transaction must reference the original via reversalOfId.',
    );
  }
  if (rev.accountId !== orig.accountId) {
    throw new RuleViolationError(
      'ledger.reversal_account_mismatch',
      'Reversal must post to the same account as the original.',
    );
  }
  if (rev.amountPaise !== orig.amountPaise) {
    throw new RuleViolationError(
      'ledger.reversal_amount_mismatch',
      'Reversal amount must match the original.',
    );
  }
  if (rev.direction === orig.direction) {
    throw new RuleViolationError(
      'ledger.reversal_direction_mismatch',
      'Reversal must have the opposite direction of the original.',
    );
  }
}

/** Signed value of a transaction: +amount for in, -amount for out. */
export function signed(txn: { amountPaise: bigint; direction: Direction }): bigint {
  return txn.direction === 'in' ? txn.amountPaise : -txn.amountPaise;
}

/** Convenience predicate: an orig + rev pair nets to zero across signed values. */
export function netsToZeroPair(orig: TxnLite, rev: TxnLite): boolean {
  return signed(orig) + signed(rev) === 0n;
}

/**
 * Mask a bank/cash account number for display. Keeps last 4 chars, replaces
 * the rest with 'X'. Rejects inputs shorter than 4.
 */
export function maskAccountNumber(full: string): string {
  const s = full.replace(/\s+/g, '');
  if (s.length < 4) {
    throw new RuleViolationError(
      'ledger.account_number_too_short',
      'Account number must be at least 4 characters.',
    );
  }
  return 'XXXX' + s.slice(-4);
}

// ----- Paste-grid parser -----

export interface PastedRow {
  line: number;
  businessDate: BusinessDate;
  amountPaise: Paise;
  direction: Direction;
  description: string;
  categoryHint?: string;
}

export interface PastedRowError {
  line: number;
  message: string;
}

export interface ParsePastedResult {
  ok: PastedRow[];
  errors: PastedRowError[];
}

const MAX_PASTED_ROWS = 200;
const HEADER_ALIASES: Record<string, string> = {
  'date': 'date',
  'txn date': 'date',
  'transaction date': 'date',
  'business date': 'date',
  'amount': 'amount',
  'amt': 'amount',
  'value': 'amount',
  'direction': 'direction',
  'type': 'direction',
  'dr/cr': 'direction',
  'description': 'description',
  'narration': 'description',
  'particulars': 'description',
  'category': 'category',
};

function normalizeDirection(raw: string): Direction | null {
  const s = raw.trim().toLowerCase();
  if (s === 'in' || s === 'credit' || s === 'cr' || s === '+') return 'in';
  if (s === 'out' || s === 'debit' || s === 'dr' || s === '-') return 'out';
  return null;
}

function parseDate(raw: string): BusinessDate | null {
  const s = raw.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return toBusinessDate(s);
  const m1 = s.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
  if (m1) return toBusinessDate(`${m1[3]}-${m1[2]}-${m1[1]}`);
  return null;
}

function parseAmount(raw: string): Paise | null {
  const s = raw.trim().replace(/[₹,\s]/g, '');
  if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
  try {
    return rupeesToPaise(Number(s));
  } catch {
    return null;
  }
}

/**
 * Parse tab-separated (Excel/Sheets clipboard) rows into typed transactions.
 * Header row optional: if the first non-blank row looks like headers, it's
 * used to map columns; otherwise columns are assumed positional in order
 * [date, amount, direction, description, category?].
 */
export function parsePastedRows(text: string): ParsePastedResult {
  const ok: PastedRow[] = [];
  const errors: PastedRowError[] = [];
  const rawLines = text.split(/\r?\n/);

  // Trim trailing empties.
  while (rawLines.length && rawLines[rawLines.length - 1]!.trim() === '') rawLines.pop();

  // Skip leading blank lines before header detection.
  let startIdx = 0;
  while (startIdx < rawLines.length && rawLines[startIdx]!.trim() === '') startIdx++;
  if (startIdx >= rawLines.length) return { ok, errors };

  // Detect header — require the first non-blank line to contain AT LEAST ONE
  // recognized header alias (so a real data row isn't mistaken for headers).
  let columnOrder: string[] = ['date', 'amount', 'direction', 'description', 'category'];
  const firstCells = rawLines[startIdx]!.split('\t').map((c) => c.trim().toLowerCase());
  const hasAnyAlias = firstCells.some((c) => c in HEADER_ALIASES);
  const allCellsHeaderLike = firstCells.every(
    (c) => c in HEADER_ALIASES || c === 'category' || c === '' || c === 'sr' || c === 'sr no',
  );
  if (hasAnyAlias && allCellsHeaderLike) {
    columnOrder = firstCells.map((c) => HEADER_ALIASES[c] ?? c);
    startIdx += 1;
  }

  const dataLines = rawLines.slice(startIdx);
  if (dataLines.length > MAX_PASTED_ROWS) {
    errors.push({
      line: MAX_PASTED_ROWS + 1,
      message: `Paste exceeds ${MAX_PASTED_ROWS} rows — split the paste and try again.`,
    });
    return { ok, errors };
  }

  for (let i = 0; i < dataLines.length; i++) {
    const lineNo = i + 1 + startIdx;
    const raw = dataLines[i]!;
    if (raw.trim() === '') continue;

    const cells = raw.split('\t');
    const getCol = (name: string): string => {
      const idx = columnOrder.indexOf(name);
      if (idx < 0 || idx >= cells.length) return '';
      return cells[idx] ?? '';
    };

    const dateStr = getCol('date');
    const amountStr = getCol('amount');
    const directionStr = getCol('direction');
    const description = getCol('description').trim();
    const categoryHint = getCol('category').trim() || undefined;

    if (!dateStr) {
      errors.push({ line: lineNo, message: 'missing date' });
      continue;
    }
    const date = parseDate(dateStr);
    if (!date) {
      errors.push({ line: lineNo, message: `invalid date "${dateStr}" (use YYYY-MM-DD or DD/MM/YYYY)` });
      continue;
    }

    if (!amountStr) {
      errors.push({ line: lineNo, message: 'missing amount' });
      continue;
    }
    const amountP = parseAmount(amountStr);
    if (amountP === null) {
      errors.push({ line: lineNo, message: `invalid amount "${amountStr}"` });
      continue;
    }
    if (amountP < 0n) {
      errors.push({ line: lineNo, message: 'amount must be positive; use the direction column for sign' });
      continue;
    }
    if (amountP === paise(0)) {
      errors.push({ line: lineNo, message: 'amount cannot be zero' });
      continue;
    }

    const dir = normalizeDirection(directionStr);
    if (!dir) {
      errors.push({
        line: lineNo,
        message: `invalid direction "${directionStr}" (use in/out, credit/debit, cr/dr, or +/-)`,
      });
      continue;
    }

    if (!description) {
      errors.push({ line: lineNo, message: 'missing description' });
      continue;
    }

    ok.push({
      line: lineNo,
      businessDate: date,
      amountPaise: amountP,
      direction: dir,
      description,
      categoryHint,
    });
  }

  return { ok, errors };
}
